import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { familyService, getLocalizedValue } from '@/services/api';
import { useAuth } from './AuthContext';

const FamilyContext = createContext(null);

export const FamilyProvider = ({ children }) => {
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  // Fetch family members (patients) from API
  const fetchFamilyMembers = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      // Load from localStorage for non-authenticated users
      const savedMembers = localStorage.getItem('familyMembers');
      if (savedMembers) {
        try {
          const parsed = JSON.parse(savedMembers);
          setFamilyMembers(parsed);
          setSelectedMember(parsed.find(m => m.isDefault) || parsed[0]);
        } catch (e) {
          console.error('Failed to parse family members:', e);
        }
      }
      return;
    }

    setLoading(true);
    try {
      const response = await familyService.getAll();
      const data = response.data?.data || response.data || [];
      
      // Transform API patients to family members format
      const transformedMembers = Array.isArray(data) ? data.map(patient => ({
        id: patient.id.toString(),
        name: `${getLocalizedValue(patient.first_name, 'en') || patient.first_name || ''} ${getLocalizedValue(patient.last_name, 'en') || patient.last_name || ''}`.trim(),
        first_name: getLocalizedValue(patient.first_name, 'en') || patient.first_name,
        last_name: getLocalizedValue(patient.last_name, 'en') || patient.last_name,
        relationship: patient.id === user.patient_id ? 'self' : (patient.relationship || 'other'),
        dateOfBirth: patient.date_of_birth || '',
        gender: patient.gender || '',
        bloodGroup: patient.blood_group || '',
        phone: patient.phone_number || '',
        isDefault: patient.is_default || patient.id === user.patient_id,
        weight: patient.weight,
        height: patient.height,
        userId: patient.user_id,
      })) : [];
      
      setFamilyMembers(transformedMembers);
      
      // Set default/selected member
      const defaultMember = transformedMembers.find(m => m.isDefault) || transformedMembers[0];
      setSelectedMember(defaultMember);
      
      // Also save to localStorage as backup
      localStorage.setItem('familyMembers', JSON.stringify(transformedMembers));
    } catch (error) {
      console.error('Failed to fetch family members:', error);
      // Fallback to localStorage
      const savedMembers = localStorage.getItem('familyMembers');
      if (savedMembers) {
        try {
          const parsed = JSON.parse(savedMembers);
          setFamilyMembers(parsed);
          setSelectedMember(parsed.find(m => m.isDefault) || parsed[0]);
        } catch (e) {
          console.error('Failed to parse family members:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Load family members on mount and when auth changes
  useEffect(() => {
    fetchFamilyMembers();
  }, [fetchFamilyMembers]);

  // Save to localStorage whenever family members change
  useEffect(() => {
    if (familyMembers.length > 0) {
      localStorage.setItem('familyMembers', JSON.stringify(familyMembers));
    }
  }, [familyMembers]);

  const addFamilyMember = async (member) => {
    // Parse name into first and last name
    const nameParts = (member.name || '').trim().split(' ');
    const firstName = nameParts[0] || 'Unknown';
    const lastName = nameParts.slice(1).join(' ') || 'Member';
    
    // Calculate age from date of birth
    let age = '0';
    if (member.dateOfBirth) {
      const birthDate = new Date(member.dateOfBirth);
      const today = new Date();
      age = String(Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000)));
    }
    
    // Create patient via API with ALL required fields
    const patientData = {
      first_name: firstName,
      last_name: lastName,
      date_of_birth: member.dateOfBirth,
      gender: member.gender || 'male',
      blood_type: member.bloodGroup || '',
      phone_number: member.phone || '',
      user_id: user?.id,
      // Required fields that backend expects
      age: age,
      weight: member.weight || '70',  // Default weight in kg
      height: member.height || '170', // Default height in cm
      alcohol: member.alcohol || false,
      smoker: member.smoker || false,
      is_default: false,
    };
    
    try {
      const response = await familyService.add(patientData);
      const newPatient = response.data?.data || response.data;
      
      const newMember = {
        id: newPatient?.id?.toString() || Date.now().toString(),
        name: member.name,
        first_name: firstName,
        last_name: lastName,
        relationship: member.relationship,
        dateOfBirth: member.dateOfBirth,
        gender: member.gender,
        bloodGroup: member.bloodGroup,
        phone: member.phone,
        weight: member.weight || '70',
        height: member.height || '170',
        alcohol: member.alcohol || false,
        smoker: member.smoker || false,
        isDefault: false,
      };
      
      setFamilyMembers(prev => [...prev, newMember]);
      return newMember;
    } catch (error) {
      console.error('Failed to add family member:', error);
      throw error; // Re-throw so the UI can show the error
    }
  };

  const updateFamilyMember = async (id, updates) => {
    // Optimistically update UI
    setFamilyMembers(prev => 
      prev.map(member => 
        member.id === id ? { ...member, ...updates } : member
      )
    );

    // Sync with API if authenticated
    if (isAuthenticated) {
      try {
        await familyService.update(id, {
          first_name: updates.name?.split(' ')[0] || updates.first_name,
          last_name: updates.name?.split(' ').slice(1).join(' ') || updates.last_name,
          date_of_birth: updates.dateOfBirth,
          gender: updates.gender,
          blood_group: updates.bloodGroup,
          phone_number: updates.phone,
        });
      } catch (error) {
        console.error('Failed to update family member:', error);
      }
    }
  };

  const removeFamilyMember = async (id) => {
    const previousMembers = familyMembers;
    
    // Optimistically update UI
    setFamilyMembers(prev => prev.filter(member => member.id !== id));
    if (selectedMember?.id === id) {
      setSelectedMember(familyMembers.find(m => m.id !== id));
    }

    // Sync with API if authenticated
    if (isAuthenticated) {
      try {
        await familyService.delete(id);
      } catch (error) {
        console.error('Failed to delete family member:', error);
        // Revert on failure
        setFamilyMembers(previousMembers);
      }
    }
  };

  const setDefaultMember = (id) => {
    setFamilyMembers(prev => 
      prev.map(member => ({
        ...member,
        isDefault: member.id === id
      }))
    );
  };

  const selectMemberForBooking = (member) => {
    setSelectedMember(member);
  };

  const value = {
    familyMembers,
    selectedMember,
    loading,
    addFamilyMember,
    updateFamilyMember,
    removeFamilyMember,
    setDefaultMember,
    selectMemberForBooking,
    refreshFamilyMembers: fetchFamilyMembers,
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
};

export const useFamily = () => {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
};
